<template>
  <div>
    <app-legend
      v-for="(legend, index) in legends"
      :key="index"
      :src="legend.url"
      :isVisible="isUp && !isEnding"
      :transformation="legend.transform"
      :position="{x: lettersPos[legend.index].x, y: lettersPos[legend.index].y, z: lettersPos[legend.index].z}"
    />
  </div>
</template>

<script>
import {
  PlaneBufferGeometry,
  MeshPhongMaterial,
  Vector2,
  Vector3,
  AxisHelper,
  CatmullRomCurve3,
  Line,
  LineBasicMaterial,
  BufferGeometry,
  Mesh
} from 'three'

import { webGL } from '@/webGL/WebGL'
import { Ball } from '@/webGL/objects/Ball'
import { Letters } from '@/webGL/objects/Letters'
import { EnvLetters } from '@/webGL/objects/EnvLetters'

// import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

import { FitTo } from '@/utils/FitTo'
import { Loader } from '@/utils/Loader'
import { VirtualScroll } from '@/utils/VirtualScroll'

import gsap from 'gsap'

import Test from '@/components/3D/Test'
import AppLegend from '@/components/3D/AppLegend'

import letters from '@/assets/data/letters'
import curve from '@/assets/data/curve'

const ballBase = -2

export default {
  components: {
    AppLegend,
    Test
  },

  data () {
    return {
      isUp: false,
      lettersPos: letters.map(letter => {
        return new Vector3(letter.position.x, letter.position.y, letter.position.z)
      }),
      isEnding: false
    }
  },

  computed: {
    legends () {
      return this.$store.state.global.letterLegends
    }
  },

  created () {
    this.fitTo = new FitTo(webGL.camera)

    let positions = curve.map(pos => { return new Vector3(pos[0] + .38, .1, -pos[1] + 21.58) })
    this.curve = new CatmullRomCurve3(positions, false)

    webGL.renderer.bloom = 0

    // const curveHelper = new Line(new BufferGeometry().setFromPoints(this.curve.getPoints(500)), new LineBasicMaterial({color: 0xff0000}))
    // webGL.scene.add(curveHelper)

    this.baseValue = -.5
  },

  mounted () {
    this.virtualScroll = new VirtualScroll({ clamp: { min: 0 } })

    this.ball = new Ball(.1)
    webGL.scene.add(this.ball)
    webGL.camera.setFocalLength(120)

    this.pointer = new Vector2(this.$device.width * .5, this.$device.height * .5)
    this.deltaPointer = new Vector2(0, 0)
    this.$root.$el.addEventListener(this.$device.pointermove, this.onPointermove)

    this.letters = new Letters(this.$device.getIntersectPosition)
    webGL.scene.add(this.letters)

    this.env = new EnvLetters()

    Promise.all([this.letters.promise, this.env.promise]).then(() => {
      this.setup()
      this.onResize()
      this.virtualScroll.onSmoothScroll(this.onSmoothScroll)

      this.$emit('ready', true)
    })

    // new OrbitControls(webGL.camera, this.$root.$el)
  },

  beforeDestroy () {
    this.virtualScroll.onDestroy()

    webGL.camera.setFocalLength(60)
    this.letters.onDestroy()

    this.$root.$el.removeEventListener(this.$device.pointermove, this.onPointermove)

    this.env.traverse(child => {
      if (child.isMesh) {
        child.material.dispose()
        child.geometry.dispose()
      }
    })

    this.ball.onDestroy()
    this.env.onDestroy()

    webGL.scene.remove(this.letters)
    webGL.scene.remove(this.ball)
  },

  methods: {
    onSmoothScroll ({ value })  {
      value *= .00025

      const pos = this.curve.getPoint(value)
      this.ball.object.position.x = pos.x
      this.ball.object.position.z = pos.z

      if (value > 0.205) {
        this.isUp = true
      }
      else {
        this.isUp = false
      }

      if (value > 0.6) {
        this.isEnding = true
      }
      else {
        this.isEnding = false
      }
    },
    onPointermove (event) {
      const pointer = this.$device.getPointerPosition(event)
      this.deltaPointer.x = this.pointer.x - pointer.x
      this.deltaPointer.y = this.pointer.y - pointer.y

      this.letters.onPointermove(this.deltaPointer)

      this.pointer = pointer
    },

    setup () {
      // this.ball.object.position.y = this.ball.radius
      this.ball.object.position.x = ballBase
      this.ball.object.position.z = 1.5
    },

    onResize () {
      let fit = null

      fit = this.fitTo.fit(this.letters.boxFull, {
        top: 1.2,
        right: 1,
        bottom: .6,
        left: 1
      }, {vector: new Vector3(0, 0, 1)})
      this.base = {...fit}

      fit = this.fitTo.fit(this.letters.boxFull, {
        top: .6,
        right: 5.5,
        bottom: .6,
        left: .5
      }, { vector: new Vector3(0, 0, 1) })
      this.reduced = {...fit}

      if (this.isEnding) {
        webGL.camera.position.copy(this.reduced.position)
        webGL.camera.look.copy(this.reduced.look)
      }
      else {
        webGL.camera.position.copy(this.base.position)
        webGL.camera.look.copy(this.base.look)
      }
      webGL.camera.forceUpdateLook = true
    },

    cameraEase (pos, look) {
      gsap.killTweensOf(webGL.camera.position, 'x,y,z')
      gsap.killTweensOf(webGL.camera.look, 'x,y,z')

      gsap.to(webGL.camera.position, {
        x: pos.x,
        y: pos.y,
        z: pos.z,
        duration: 2,
        ease: 'power3.inOut'
      })
      gsap.to(webGL.camera.look, {
        x: look.x,
        y: look.y,
        z: look.z,
        duration: 2,
        ease: 'power3.inOut'
      })
    }
  },

  watch: {
    isEnding () {
      this.$emit('ending', this.isEnding)

      if (this.isEnding) {
        this.cameraEase(this.reduced.position, this.reduced.look)
      }
      else {
        this.cameraEase(this.base.position, this.base.look)
      }
    },
    isUp () {
      if (this.isUp) {
        this.letters.onUp()
        this.env.onUp()
      }
      else {
        this.letters.onDown()
        this.env.onDown()
      }
    }
  }
}
</script>